import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import collectionApiClient, {
  CollectionUnion,
  collectionFilters,
} from '../../../api/collections';
import {
  CollectionFilterTab,
  collectionFilterTabToApiFilters,
} from '../types/filterTypes';

const COLLECTION_PAGE_SIZE = 12;

const fetchCollections = async (
  pageParam: { skip?: number; limit?: number; filters?: collectionFilters },
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<CollectionUnion[]> => {
  const { skip = 0, limit, filters } = pageParam;
  const res = await collectionApiClient.getCollections({
    options: {
      skip: skip || 0,
      limit: limit || COLLECTION_PAGE_SIZE,
      filters,
    },
  });

  res.forEach((collection) => {
    queryClient.setQueryData(['collection', collection.id], collection);
  });

  return res;
};

const useCollectionsList = (filters?: collectionFilters) => {
  const [activeFilter, setActiveFilter] = useState<CollectionFilterTab>(
    CollectionFilterTab.All,
  );
  const dataRef = useRef<CollectionUnion[] | undefined>(undefined);
  const queryClient = useQueryClient();

  const handleFilterChange = useCallback((filter: CollectionFilterTab) => {
    setActiveFilter(filter);
  }, []);

  const combinedFilters =
    filters || collectionFilterTabToApiFilters[activeFilter];

  const { data, fetchNextPage, ...rest } = useInfiniteQuery<
    CollectionUnion[],
    Error
  >({
    queryKey: ['collections', combinedFilters],
    queryFn: ({ pageParam }) =>
      fetchCollections(
        {
          ...(pageParam as { skip?: number; limit?: number }),
          filters: combinedFilters,
        },
        queryClient,
      ),
    getNextPageParam: (lastPage, allPages) => {
      const currentLength = allPages?.flat()?.length;
      return currentLength < COLLECTION_PAGE_SIZE
        ? undefined
        : { skip: currentLength, filters: combinedFilters };
    },
    initialPageParam: { skip: 0, filters: combinedFilters },
  });
  const flatData = data?.pages.flat();

  useEffect(() => {
    dataRef.current = flatData;
  }, [flatData]);

  return {
    data: flatData,
    dataRef,
    fetchNextPage,
    activeFilter,
    handleFilterChange,
    ...rest,
  };
};
export default useCollectionsList;
